import React, { useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { useMutation } from '@apollo/client';

import { SmallLoader } from 'shared/components';
import useErnie from 'shared/hooks/use-ernie';
import { isReferralsRoute as isReferralsRouteUtils } from 'src/components/loyalty/rewards/dpos-utils';
import { detectLoyaltyIntegration, useLoyalty, Statuses } from 'src/components/loyalty/use-loyalty';
import { DialogContainer } from 'src/components/modals/components/simple-dialog';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import { useSwitchToOtherAccount } from 'src/hooks/use-switch-to-other-account';
import { useObserver } from 'mobx-react-lite';
import { FindOrCreateReferralCodeDocument } from 'src/gql/graphql';
import type { LoyaltyEnrollmentSurface } from 'src/utils/analytics/events';
import { Connect, ReferralConnect } from './connect';
import { Pin } from './pin';
import { Success } from './success';
import { Terms } from './terms';
import { InlinePhoneVerification } from './inline-phone-verification';

type LoyaltyProps = {
  onClose: (result?: { status: Statuses }) => void;
  surface: LoyaltyEnrollmentSurface;
};

export const Loyalty = ({ onClose, surface }: LoyaltyProps): JSX.Element => {
  const { dispensary } = useDispensary();
  const User = useUser();
  const phone = useObserver(() => User.phone);

  // Check if we're on a referrals route
  const router = useRouter();
  const showErnie = useErnie();
  const isReferralsRoute = router ? isReferralsRouteUtils(router) : false;
  const referralCode = router?.isReady ? String(router.query.code) : '';

  const [findOrCreateReferralCodeMutation, { data: findOrCreateReferralCodeData }] = useMutation(
    FindOrCreateReferralCodeDocument
  );

  useEffect(() => {
    if (isReferralsRoute && referralCode && dispensary?.id) {
      const fetchReferralData = async (): Promise<void> => {
        try {
          await findOrCreateReferralCodeMutation({
            variables: {
              dispensaryId: dispensary.id,
              lspName: dispensary.dposLspName ?? '',
              code: referralCode,
            },
          });
        } catch (error) {
          // Handle error silently or log it
          showErnie('Error fetching referral data. Please try again.', 'danger');
        }
      };
      void fetchReferralData();
    }
  }, [
    dispensary?.dposLspName,
    dispensary?.id,
    findOrCreateReferralCodeMutation,
    referralCode,
    isReferralsRoute,
    showErnie,
  ]);

  const { status, setStatus, programName, programDescription, createUserAndPin, processPin, resendPin } = useLoyalty({
    initialStatus: Statuses.signup,
    integration: detectLoyaltyIntegration(dispensary),
    surface,
    referralData: findOrCreateReferralCodeData?.findOrCreateReferralCode ?? null,
  });

  const switchToOtherAccount = useSwitchToOtherAccount();

  const handleClose = (): void => {
    onClose({ status });
  };
  return (
    <DialogContainer>
      {status === Statuses.loading && <SmallLoader data-testid='loyalty-modal-loader' />}
      {status === Statuses.signup &&
        (isReferralsRoute ? (
          <ReferralConnect
            onClose={onClose}
            setStatus={setStatus}
            programName={programName}
            programDescription={programDescription}
          />
        ) : (
          <Connect
            onClose={handleClose}
            setStatus={setStatus}
            programName={programName}
            programDescription={programDescription}
          />
        ))}

      {status === Statuses.terms && (
        <Terms onClose={handleClose} programName={programName} createUserAndPin={createUserAndPin} />
      )}
      {status === Statuses.verificationRequired && (
        <InlinePhoneVerification
          dispensaryId={dispensary?.id}
          enterpriseId={dispensary?.retailer?.enterpriseId}
          initialPhone={phone}
          onVerified={() => {
            void createUserAndPin({ afterVerification: true });
          }}
          onClose={handleClose}
          onLoginToOtherAccount={() => {
            // Close first so the login modal doesn't open on top of this one.
            handleClose();
            void switchToOtherAccount();
          }}
        />
      )}
      {[Statuses.pin, Statuses.incorrectPin].includes(status) && !isReferralsRoute && (
        <Pin
          onClose={handleClose}
          status={status}
          programName={programName}
          processPin={processPin}
          resendPin={resendPin}
        />
      )}
      {status === Statuses.success && !isReferralsRoute && <Success onClose={handleClose} programName={programName} />}
    </DialogContainer>
  );
};
